// frontend/src/pages/MonitoringPage.js
import React, { useState, useEffect, useCallback } from 'react';
import useApi from '../hooks/useApi';

// Função para garantir que a data inicial obedeça o Fuso Horário Local (Brasil) e não o UTC
const getLocalYYYYMMDD = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const MonitoringPage = () => {
    const { apiFetch } = useApi();
    
    const [logs, setLogs] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [comparativo, setComparativo] = useState([]);
    const [marcacoes, setMarcacoes] = useState([]); 
    const [apenasSessoes, setApenasSessoes] = useState(false); 
    
    const [dataInicio, setDataInicio] = useState(getLocalYYYYMMDD());
    const [dataFim, setDataFim] = useState(getLocalYYYYMMDD());
    
    const [usuarioSelecionado, setUsuarioSelecionado] = useState('todos');
    const [loading, setLoading] = useState(false);
    
    // NOVO: Estado para gerenciar qual campo o Admin está editando manualmente
    const [editMode, setEditMode] = useState(null); 

    const [webOnlineUsers, setWebOnlineUsers] = useState([]);

    useEffect(() => {
        const fetchOnlineUsers = async () => {
            try {
                const res = await apiFetch('/monitor/web-online');
                if (Array.isArray(res)) setWebOnlineUsers(res);
            } catch (e) {
                console.error("Erro ao buscar usuários web online:", e);
            }
        };

        fetchOnlineUsers();
        const interval = setInterval(fetchOnlineUsers, 10000);
        return () => clearInterval(interval);
    }, [apiFetch]);

    const fetchUsuarios = useCallback(async () => {
        try {
            const data = await apiFetch('/monitor/users');
            setUsuarios(data || []);
        } catch (e) { console.error("Erro ao buscar usuários:", e); }
    }, [apiFetch]);

    const fetchDashboardData = useCallback(async () => {
        setLoading(true);
        try {
            const query = new URLSearchParams({
                dataInicio,
                dataFim,
                usuario_id: usuarioSelecionado
            }).toString();

            const response = await apiFetch(`/monitor/dashboard?${query}`);
            const mRes = await apiFetch(`/monitor/marcacoes?dataInicio=${dataInicio}&dataFim=${dataFim}&usuario_id=${usuarioSelecionado}`);
            
            let rawLogs = response.logs || [];

            const blockEvents = rawLogs.filter(l => l.janela_ativa.includes('Tentativa de login fora do horário'));
            
            let filteredLogs = rawLogs.filter(l => {
                if (l.janela_ativa.includes('INÍCIO DE SESSÃO') || l.janela_ativa.includes('FIM DE SESSÃO')) {
                    const lTime = new Date(l.hora_registro.replace(' ', 'T')).getTime();
                    
                    const isBlocked = blockEvents.some(b => {
                        if (b.nome_funcionario !== l.nome_funcionario) return false;
                        const bTime = new Date(b.hora_registro.replace(' ', 'T')).getTime();
                        return Math.abs(bTime - lTime) <= 10000; 
                    });
                    
                    if (isBlocked) return false; 
                }
                return true;
            });

            filteredLogs = filteredLogs.map(l => {
                if (l.janela_ativa.includes('Tentativa de login')) {
                    return { ...l, status_atividade: 'Acesso Bloqueado' };
                }
                if (l.janela_ativa.includes('Saída forçada pelo gestor')) {
                    return { ...l, status_atividade: 'Desconexão Forçada' };
                }
                return l;
            });

            setLogs(filteredLogs);
            setComparativo(response.comparativo || []);
            setMarcacoes(mRes || []); 
        } catch (e) { 
            console.error("Erro ao buscar dados do dashboard:", e); 
        } finally {
            setLoading(false);
        }
    }, [apiFetch, dataInicio, dataFim, usuarioSelecionado]);

    const handleMarcacao = async (sessao_id, tipo_marcacao, usuario_id) => {
        if (!sessao_id || !usuario_id) {
            alert("Erro interno: ID da sessão ou usuário não encontrado nesta linha.");
            return;
        }

        try {
            await apiFetch('/monitor/marcacoes', {
                method: 'POST',
                body: {
                    sessao_id,
                    tipo_marcacao,
                    usuario_id,
                    data_referencia: dataInicio
                }
            });
            fetchDashboardData(); 
        } catch (e) {
            console.error("Erro detalhado do servidor:", e);
            alert("Falha ao gravar no servidor: " + (e.message || e.error || "Erro desconhecido."));
        }
    };

    // NOVO: Função para salvar o horário digitado manualmente (ignora o ID de sessão)
    const handleMarcacaoManual = async (tipo_marcacao, hora_digitada) => {
        if (usuarioSelecionado === 'todos') {
            alert("Selecione um funcionário específico para inserir a hora manual.");
            return;
        }
        if (!hora_digitada) return;

        try {
            await apiFetch('/monitor/marcacoes', {
                method: 'POST',
                body: {
                    sessao_id: null,
                    tipo_marcacao,
                    usuario_id: usuarioSelecionado,
                    data_referencia: dataInicio,
                    hora_manual: hora_digitada 
                }
            });
            fetchDashboardData(); 
        } catch (e) {
            console.error("Erro detalhado do servidor:", e);
            alert("Falha ao gravar no servidor: " + (e.message || e.error || "Erro desconhecido."));
        }
    };

    useEffect(() => {
        fetchUsuarios();
    }, [fetchUsuarios]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    const calcularPorcentagem = (horas) => {
        const inicio = new Date(dataInicio);
        const fim = new Date(dataFim);
        const diasTotais = Math.max(1, Math.ceil((fim - inicio) / (1000 * 60 * 60 * 24)) + 1);
        
        const meta = diasTotais * 8; 
        const calc = (horas / meta) * 100;
        return calc > 100 ? 100 : calc;
    };

    const totalLogs = logs.filter(l => ['Produtivo', 'Improdutivo', 'Fora do Horário', 'Ocioso', 'Inativo'].includes(l.status_atividade)).length;
    const produtivoCount = logs.filter(l => l.status_atividade === 'Produtivo').length;
    const improdutivoCount = logs.filter(l => l.status_atividade === 'Improdutivo').length;
    const ociosoCount = logs.filter(l => l.status_atividade === 'Ocioso').length;
    const inativoCount = logs.filter(l => l.status_atividade === 'Inativo').length;
    const foraCount = logs.filter(l => l.status_atividade === 'Fora do Horário').length;

    const prodPct = totalLogs > 0 ? (produtivoCount / totalLogs) * 100 : 0;
    const impPct = totalLogs > 0 ? (improdutivoCount / totalLogs) * 100 : 0;
    const ociosoPct = totalLogs > 0 ? (ociosoCount / totalLogs) * 100 : 0;
    const inativoPct = totalLogs > 0 ? (inativoCount / totalLogs) * 100 : 0;
    const foraPct = totalLogs > 0 ? (foraCount / totalLogs) * 100 : 0;

    let currentPct = 0;
    const gradientParts = [];
    if (prodPct > 0) { gradientParts.push(`#16a34a ${currentPct}% ${currentPct + prodPct}%`); currentPct += prodPct; }
    if (impPct > 0) { gradientParts.push(`#dc2626 ${currentPct}% ${currentPct + impPct}%`); currentPct += impPct; }
    if (ociosoPct > 0) { gradientParts.push(`#f97316 ${currentPct}% ${currentPct + ociosoPct}%`); currentPct += ociosoPct; }
    if (inativoPct > 0) { gradientParts.push(`#9ca3af ${currentPct}% ${currentPct + inativoPct}%`); currentPct += inativoPct; }
    if (foraPct > 0) { gradientParts.push(`#eab308 ${currentPct}% ${currentPct + foraPct}%`); currentPct += foraPct; }

    const pieGradient = totalLogs > 0 && gradientParts.length > 0
        ? `conic-gradient(${gradientParts.join(', ')})`
        : `conic-gradient(#e5e7eb 0% 100%)`;

    const formatarMinutos = (minutos) => {
        const h = Math.floor(minutos / 60);
        const m = Math.round(minutos % 60);
        if (h > 0) return `${h}h ${m}m`;
        return `${m}m`;
    };

    const formatarHorasDecimais = (horasDecimais) => {
        const minutesTotais = Math.round(parseFloat(horasDecimais || 0) * 60);
        return formatarMinutos(minutesTotais);
    };

    const displayedLogs = usuarioSelecionado === 'todos' 
        ? logs 
        : logs.filter(log => {
            const selectedUser = usuarios.find(u => String(u.id) === String(usuarioSelecionado));
            return selectedUser && (log.nome_funcionario === selectedUser.nome_funcionario || log.nome_funcionario === selectedUser.login);
        });

    const displayedLogsFiltered = apenasSessoes 
        ? displayedLogs.filter(l => l.status_atividade === 'Acesso / Login' || l.status_atividade === 'Saída / Logout')
        : displayedLogs;

    // --- LÓGICA DE ESPELHO DE PONTO ELETRÔNICO ---
    let horaEntrada = '--:--';
    let horaSaidaAlmoco = '--:--';
    let horaRetornoAlmoco = '--:--';
    let horaSaidaFinal = '--:--';
    let duracaoAlmoco = '';
    let atraso = ''; 

    const isDiario = dataInicio === dataFim; 

    if (isDiario) {
        const chronLogs = [...displayedLogs].sort((a, b) => a.hora_registro.localeCompare(b.hora_registro));
        
        const logins = chronLogs.filter(l => l.status_atividade === 'Acesso / Login');
        const logouts = chronLogs.filter(l => l.status_atividade === 'Saída / Logout');

        if (logins.length > 0) horaEntrada = logins[0].hora_registro.split(' ')[1] || logins[0].hora_registro;
        if (logouts.length > 0) horaSaidaFinal = logouts[logouts.length - 1].hora_registro.split(' ')[1] || logouts[logouts.length - 1].hora_registro;
        if (logouts.length > 0) horaSaidaAlmoco = logouts[0].hora_registro.split(' ')[1] || logouts[0].hora_registro;
        if (logins.length > 1) horaRetornoAlmoco = logins[1].hora_registro.split(' ')[1] || logins[1].hora_registro;

        const marcEntrada = marcacoes.find(m => m.tipo_marcacao === 'entrada');
        if (marcEntrada) {
            // Se houver hora digitada, sobrescreve o comportamento automático
            if (marcEntrada.hora_manual) {
                horaEntrada = marcEntrada.hora_manual;
            } else {
                const logE = chronLogs.find(l => String(l.sessao_id) === String(marcEntrada.sessao_id) && l.status_atividade === 'Acesso / Login');
                if (logE) horaEntrada = logE.hora_registro.split(' ')[1] || logE.hora_registro;
            }
        }

        const marcAlmoco = marcacoes.find(m => m.tipo_marcacao === 'almoco');
        if (marcAlmoco) {
            if (marcAlmoco.hora_manual) {
                horaSaidaAlmoco = marcAlmoco.hora_manual;
            } else {
                const logAlmoco = chronLogs.find(l => String(l.sessao_id) === String(marcAlmoco.sessao_id) && l.status_atividade === 'Saída / Logout');
                if (logAlmoco) horaSaidaAlmoco = logAlmoco.hora_registro.split(' ')[1] || logAlmoco.hora_registro;
            }
        }

        const marcRetorno = marcacoes.find(m => m.tipo_marcacao === 'retorno_almoco');
        if (marcRetorno) {
            if (marcRetorno.hora_manual) {
                horaRetornoAlmoco = marcRetorno.hora_manual;
            } else {
                const logRetorno = chronLogs.find(l => String(l.sessao_id) === String(marcRetorno.sessao_id) && l.status_atividade === 'Acesso / Login');
                if (logRetorno) horaRetornoAlmoco = logRetorno.hora_registro.split(' ')[1] || logRetorno.hora_registro;
            }
        }

        const marcSaida = marcacoes.find(m => m.tipo_marcacao === 'saida');
        if (marcSaida) {
            if (marcSaida.hora_manual) {
                horaSaidaFinal = marcSaida.hora_manual;
            } else {
                const logS = chronLogs.find(l => String(l.sessao_id) === String(marcSaida.sessao_id) && l.status_atividade === 'Saída / Logout');
                if (logS) horaSaidaFinal = logS.hora_registro.split(' ')[1] || logS.hora_registro;
            }
        }

        if (horaSaidaAlmoco !== '--:--' && horaRetornoAlmoco !== '--:--') {
            const [hS, mS] = horaSaidaAlmoco.split(':').map(Number);
            const [hR, mR] = horaRetornoAlmoco.split(':').map(Number);
            
            const dataS = new Date(2000, 0, 1, hS || 0, mS || 0, 0);
            const dataR = new Date(2000, 0, 1, hR || 0, mR || 0, 0);

            if (dataR >= dataS) {
                const diffMs = dataR - dataS;
                const diffMins = Math.floor(diffMs / 60000);
                const hDiff = Math.floor(diffMins / 60);
                const mDiff = diffMins % 60;
                
                if (hDiff > 0) {
                    duracaoAlmoco = `${hDiff}h ${mDiff}m`;
                } else {
                    duracaoAlmoco = `${mDiff}m`;
                }
            }
        }

        if (horaEntrada !== '--:--' && usuarioSelecionado !== 'todos') {
            const selectedUser = usuarios.find(u => String(u.id) === String(usuarioSelecionado));
            if (selectedUser && selectedUser.entrada && selectedUser.entrada !== '-') {
                const scheduledEntrada = selectedUser.entrada; 
                const [hA, mA] = horaEntrada.split(':').map(Number); 
                const [hS, mS] = scheduledEntrada.split(':').map(Number); 
                
                const dataA = new Date(2000, 0, 1, hA || 0, mA || 0, 0);
                const dataS = new Date(2000, 0, 1, hS || 0, mS || 0, 0);
                
                if (dataA > dataS) {
                    const diffMs = dataA - dataS;
                    const diffMins = Math.floor(diffMs / 60000);
                    const hDiff = Math.floor(diffMins / 60);
                    const mDiff = diffMins % 60;
                    
                    if (hDiff > 0) {
                        atraso = `${hDiff}h ${mDiff}m`;
                    } else {
                        atraso = `${mDiff}m`;
                    }
                }
            }
        }
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen print:p-0 print:bg-white print:min-h-0 print:block print:h-auto">
            <style>{`
                @media print {
                    @page {
                        size: landscape;
                        margin: 10mm;
                    }
                    aside, nav, .sidebar, #sidebar, [class*="sidebar"], [class*="menu-lateral"], .print\\:hidden {
                        display: none !important;
                    }
                    html, body, #root, main, .main-content, .min-h-screen {
                        width: 100% !important;
                        height: auto !important;
                        min-h-screen: 0 !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        background: white !important;
                        overflow: visible !important;
                        position: static !important;
                    }
                    * {
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    table { page-break-inside: auto !important; }
                    tr { page-break-inside: avoid !important; page-break-after: auto !important; }
                    thead { display: table-header-group !important; }
                    tfoot { display: table-footer-group !important; }
                }
            `}</style>

            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Dashboard de Produtividade</h1>
            </div>
            
            <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200 mb-6 print:hidden">
                <div className="flex items-center gap-2 mb-3 border-b border-gray-100 pb-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]"></div>
                    <h2 className="text-lg font-bold text-gray-800">Usuários Online no DFinAI Web</h2>
                    <span className="bg-green-100 text-green-800 text-xs font-bold px-2.5 py-0.5 rounded-full ml-2">
                        {webOnlineUsers.length}
                    </span>
                </div>
                <div className="flex flex-wrap gap-3">
                    {webOnlineUsers.length > 0 ? (
                        webOnlineUsers.map(u => (
                            <div key={u.id} className="flex items-center gap-2 bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-full shadow-sm hover:shadow-md transition-shadow cursor-default">
                                <div className="w-7 h-7 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-black text-xs">
                                    {(u.name || u.username).substring(0, 2).toUpperCase()}
                                </div>
                                <span className="text-sm font-bold text-gray-700">{u.name || u.username}</span>
                            </div>
                        ))
                    ) : (
                        <p className="text-sm text-gray-500 italic px-2">Nenhum usuário ativo no portal neste momento.</p>
                    )}
                </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow border mb-6 flex flex-wrap gap-4 items-end print:hidden">
                <div className="flex-1 min-w-[180px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data Início</label>
                    <input 
                        type="date" 
                        value={dataInicio}
                        onChange={(e) => setDataInicio(e.target.value)}
                        className="w-full border-gray-300 rounded-md shadow-sm p-2 border focus:ring-indigo-500 focus:border-indigo-500"
                    />
                </div>

                <div className="flex-1 min-w-[180px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data Fim</label>
                    <input 
                        type="date" 
                        value={dataFim}
                        onChange={(e) => setDataFim(e.target.value)}
                        className="w-full border-gray-300 rounded-md shadow-sm p-2 border focus:ring-indigo-500 focus:border-indigo-500"
                    />
                </div>

                <div className="flex-1 min-w-[180px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Funcionário</label>
                    <select 
                        value={usuarioSelecionado} 
                        onChange={(e) => setUsuarioSelecionado(e.target.value)}
                        className="w-full border-gray-300 rounded-md shadow-sm p-2 border focus:ring-indigo-500 focus:border-indigo-500"
                    >
                        <option value="todos">Todos da Minha Entidade</option>
                        {usuarios.map(u => (
                            <option key={u.id} value={u.id}>{u.nome_funcionario || u.login}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <button 
                        onClick={() => window.print()} 
                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-6 rounded-md shadow transition-colors flex items-center gap-2 h-[42px]"
                    >
                        <span>🖨️</span> Imprimir Relatório
                    </button>
                </div>
            </div>

            {loading && <div className="text-center text-indigo-600 mb-4 animate-pulse print:hidden">Atualizando painel...</div>}

            <div className="bg-white p-6 rounded-lg shadow border mb-6 flex flex-col md:flex-row items-center gap-8 print:shadow-none print:border-gray-300 print:break-inside-avoid">
                <div className="flex-1 w-full">
                    <h2 className="text-lg font-bold mb-4 text-gray-800">Distribuição de Tempo (Geral)</h2>
                    {totalLogs === 0 && !loading ? (
                         <p className="text-gray-500 text-sm">Nenhum dado registrado neste período para gerar o gráfico.</p>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between border-b pb-2">
                                <div className="flex items-center gap-3">
                                    <div className="w-4 h-4 bg-green-600 rounded-full shadow-sm"></div>
                                    <span className="text-sm font-semibold text-gray-700">Horas Produtivas</span>
                                </div>
                                <span className="text-sm font-bold text-gray-900">{formatarMinutos(produtivoCount)} <span className="text-gray-500 font-normal ml-1">({prodPct.toFixed(1)}%)</span></span>
                            </div>
                            <div className="flex items-center justify-between border-b pb-2">
                                <div className="flex items-center gap-3">
                                    <div className="w-4 h-4 bg-red-600 rounded-full shadow-sm"></div>
                                    <span className="text-sm font-semibold text-gray-700">Horas Improdutivas</span>
                                </div>
                                <span className="text-sm font-bold text-gray-900">{formatarMinutos(improdutivoCount)} <span className="text-gray-500 font-normal ml-1">({impPct.toFixed(1)}%)</span></span>
                            </div>
                            <div className="flex items-center justify-between border-b pb-2">
                                <div className="flex items-center gap-3">
                                    <div className="w-4 h-4 bg-orange-500 rounded-full shadow-sm"></div>
                                    <span className="text-sm font-semibold text-gray-700">Tempo Ocioso</span>
                                </div>
                                <span className="text-sm font-bold text-gray-900">{formatarMinutos(ociosoCount)} <span className="text-gray-500 font-normal ml-1">({ociosoPct.toFixed(1)}%)</span></span>
                            </div>
                            <div className="flex items-center justify-between border-b pb-2">
                                <div className="flex items-center gap-3">
                                    <div className="w-4 h-4 bg-gray-400 rounded-full shadow-sm"></div>
                                    <span className="text-sm font-semibold text-gray-700">Tempo Inativo</span>
                                </div>
                                <span className="text-sm font-bold text-gray-900">{formatarMinutos(inativoCount)} <span className="text-gray-500 font-normal ml-1">({inativoPct.toFixed(1)}%)</span></span>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-4 h-4 bg-yellow-500 rounded-full shadow-sm"></div>
                                    <span className="text-sm font-semibold text-gray-700">Fora do Horário</span>
                                </div>
                                <span className="text-sm font-bold text-gray-900">{formatarMinutos(foraCount)} <span className="text-gray-500 font-normal ml-1">({foraPct.toFixed(1)}%)</span></span>
                            </div>
                        </div>
                    )}
                </div>
                
                <div className="flex justify-center items-center p-4">
                    <div 
                        className="w-48 h-48 rounded-full shadow-md border-4 border-white relative flex items-center justify-center transition-all duration-500"
                        style={{ background: pieGradient }}
                    >
                        <div className="w-24 h-24 bg-white rounded-full shadow-inner flex flex-col items-center justify-center">
                            <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Total</span>
                            <span className="text-sm font-bold text-gray-800">{formatarMinutos(produtivoCount + improdutivoCount + ociosoCount + inativoCount + foraCount)}</span>
                        </div>
                    </div>
                </div>
            </div>

            {isDiario && (
                <div className="bg-white p-6 rounded-lg shadow border mb-6 print:break-inside-avoid">
                    <h2 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">Marcações de Ponto (Intervenção Admin)</h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        
                        <div className="bg-green-50 p-4 rounded-lg border border-green-200 flex flex-col items-center justify-center relative group">
                            <span className="block text-xs text-green-700 font-bold uppercase mb-1">Entrada</span>
                            {editMode === 'entrada' ? (
                                <input 
                                    type="time" 
                                    autoFocus
                                    defaultValue={horaEntrada !== '--:--' ? horaEntrada : ''} 
                                    onBlur={(e) => { 
                                        if(e.target.value && e.target.value !== horaEntrada) handleMarcacaoManual('entrada', e.target.value); 
                                        setEditMode(null); 
                                    }} 
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') e.target.blur();
                                        if (e.key === 'Escape') setEditMode(null);
                                    }}
                                    className="text-xl font-black text-green-900 bg-white border border-green-300 rounded px-2 outline-none text-center w-full max-w-[120px]" 
                                />
                            ) : (
                                <div className="flex items-center gap-2">
                                    <span className="text-2xl font-black text-green-900 tracking-wider">{horaEntrada}</span>
                                    <button onClick={() => usuarioSelecionado !== 'todos' ? setEditMode('entrada') : alert("Selecione um funcionário específico para editar.")} className="opacity-0 group-hover:opacity-100 transition-opacity print:hidden text-gray-500 hover:text-green-700">✏️</button>
                                </div>
                            )}
                        </div>

                        <div className="bg-red-50 p-4 rounded-lg border border-red-200 flex flex-col items-center justify-center relative group">
                            <span className="block text-xs text-red-700 font-bold uppercase mb-1">Saída Almoço</span>
                            {editMode === 'almoco' ? (
                                <input 
                                    type="time" 
                                    autoFocus
                                    defaultValue={horaSaidaAlmoco !== '--:--' ? horaSaidaAlmoco : ''} 
                                    onBlur={(e) => { 
                                        if(e.target.value && e.target.value !== horaSaidaAlmoco) handleMarcacaoManual('almoco', e.target.value); 
                                        setEditMode(null); 
                                    }} 
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') e.target.blur();
                                        if (e.key === 'Escape') setEditMode(null);
                                    }}
                                    className="text-xl font-black text-red-900 bg-white border border-red-300 rounded px-2 outline-none text-center w-full max-w-[120px]" 
                                />
                            ) : (
                                <div className="flex items-center gap-2">
                                    <span className="text-2xl font-black text-red-900 tracking-wider">{horaSaidaAlmoco}</span>
                                    <button onClick={() => usuarioSelecionado !== 'todos' ? setEditMode('almoco') : alert("Selecione um funcionário específico para editar.")} className="opacity-0 group-hover:opacity-100 transition-opacity print:hidden text-gray-500 hover:text-red-700">✏️</button>
                                </div>
                            )}
                        </div>

                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 flex flex-col items-center justify-center relative group">
                            <span className="block text-xs text-blue-700 font-bold uppercase mb-1">Retorno Almoço</span>
                            {editMode === 'retorno_almoco' ? (
                                <input 
                                    type="time" 
                                    autoFocus
                                    defaultValue={horaRetornoAlmoco !== '--:--' ? horaRetornoAlmoco : ''} 
                                    onBlur={(e) => { 
                                        if(e.target.value && e.target.value !== horaRetornoAlmoco) handleMarcacaoManual('retorno_almoco', e.target.value); 
                                        setEditMode(null); 
                                    }} 
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') e.target.blur();
                                        if (e.key === 'Escape') setEditMode(null);
                                    }}
                                    className="text-xl font-black text-blue-900 bg-white border border-blue-300 rounded px-2 outline-none text-center w-full max-w-[120px]" 
                                />
                            ) : (
                                <div className="flex items-center gap-2">
                                    <span className="text-2xl font-black text-blue-900 tracking-wider">{horaRetornoAlmoco}</span>
                                    <button onClick={() => usuarioSelecionado !== 'todos' ? setEditMode('retorno_almoco') : alert("Selecione um funcionário específico para editar.")} className="opacity-0 group-hover:opacity-100 transition-opacity print:hidden text-gray-500 hover:text-blue-700">✏️</button>
                                </div>
                            )}
                        </div>

                        <div className="bg-gray-100 p-4 rounded-lg border border-gray-300 flex flex-col items-center justify-center relative group">
                            <span className="block text-xs text-gray-700 font-bold uppercase mb-1">Saída Final</span>
                            {editMode === 'saida' ? (
                                <input 
                                    type="time" 
                                    autoFocus
                                    defaultValue={horaSaidaFinal !== '--:--' ? horaSaidaFinal : ''} 
                                    onBlur={(e) => { 
                                        if(e.target.value && e.target.value !== horaSaidaFinal) handleMarcacaoManual('saida', e.target.value); 
                                        setEditMode(null); 
                                    }} 
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') e.target.blur();
                                        if (e.key === 'Escape') setEditMode(null);
                                    }}
                                    className="text-xl font-black text-gray-900 bg-white border border-gray-400 rounded px-2 outline-none text-center w-full max-w-[120px]" 
                                />
                            ) : (
                                <div className="flex items-center gap-2">
                                    <span className="text-2xl font-black text-gray-900 tracking-wider">{horaSaidaFinal}</span>
                                    <button onClick={() => usuarioSelecionado !== 'todos' ? setEditMode('saida') : alert("Selecione um funcionário específico para editar.")} className="opacity-0 group-hover:opacity-100 transition-opacity print:hidden text-gray-500 hover:text-gray-700">✏️</button>
                                </div>
                            )}
                        </div>
                    </div>
                    {(duracaoAlmoco || atraso) && (
                        <div className="mt-4 flex flex-wrap justify-center gap-4">
                            {duracaoAlmoco && (
                                <span className="inline-block bg-indigo-50 text-indigo-700 text-xs font-bold px-4 py-1.5 rounded-full border border-indigo-200 shadow-sm uppercase tracking-wider">
                                    Duração do Almoço: {duracaoAlmoco}
                                </span>
                            )}
                            {atraso && (
                                <span className="inline-block bg-orange-50 text-orange-700 text-xs font-bold px-4 py-1.5 rounded-full border border-orange-200 shadow-sm uppercase tracking-wider">
                                    Atraso: {atraso}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            )}

            {usuarioSelecionado === 'todos' && (
                <div className="bg-white p-6 rounded-lg shadow border mb-6 print:break-inside-avoid">
                    <h2 className="text-lg font-bold mb-4 text-gray-800">Comparativo de Horas por Funcionário</h2>
                    <div className="space-y-4">
                        {comparativo.length === 0 && !loading ? (
                            <p className="text-gray-500 text-sm">Nenhum dado produtivo registrado neste período.</p>
                        ) : (
                            comparativo.map((comp, i) => (
                                <div key={i} className="flex flex-col">
                                    <div className="flex justify-between items-end text-sm mb-1">
                                        <span className="font-semibold text-gray-800">{comp.nome_funcionario}</span>
                                        <span className="flex items-center gap-3 text-gray-600">
                                            <span className="text-blue-600 font-semibold text-xs border-r pr-3 border-gray-300">
                                                {comp.qtd_logins} Login(s) | {comp.qtd_logouts} Logout(s)
                                            </span>
                                            <span>
                                                <span className="text-green-600 font-bold">{formatarHorasDecimais(comp.horas_produtivas)} Prod</span> | 
                                                <span className="text-red-500 font-bold ml-1">{formatarHorasDecimais(comp.horas_improdutivas)} Imp</span> |
                                                <span className="text-orange-500 font-bold ml-1">{formatarHorasDecimais(comp.horas_ociosas)} Oci</span> |
                                                <span className="text-gray-500 font-bold ml-1">{formatarHorasDecimais(comp.horas_inativas)} Ina</span>
                                            </span>
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-4 flex overflow-hidden shadow-inner">
                                        <div className="bg-green-600 h-4 transition-all duration-500" style={{ width: `${calcularPorcentagem(comp.horas_produtivas)}%` }} title="Horas Produtivas"></div>
                                        <div className="bg-red-500 h-4 transition-all duration-500 opacity-90" style={{ width: `${calcularPorcentagem(comp.horas_improdutivas)}%` }} title="Horas Improdutivas"></div>
                                        <div className="bg-orange-500 h-4 transition-all duration-500 opacity-90" style={{ width: `${calcularPorcentagem(comp.horas_ociosas)}%` }} title="Tempo Ocioso"></div>
                                        <div className="bg-gray-400 h-4 transition-all duration-500 opacity-90" style={{ width: `${calcularPorcentagem(comp.horas_inativas)}%` }} title="Tempo Inativo"></div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            <div className="bg-white rounded-lg shadow border overflow-hidden print:overflow-visible print:border-none print:shadow-none print:h-auto print:block">
                <div className="p-4 border-b bg-gray-50 flex justify-between items-center flex-wrap gap-4">
                    <h2 className="text-lg font-bold text-gray-800">Histórico de Atividades Detalhado</h2>
                    <label className="flex items-center space-x-2 cursor-pointer print:hidden">
                        <input 
                            type="checkbox" 
                            checked={apenasSessoes} 
                            onChange={(e) => setApenasSessoes(e.target.checked)} 
                            className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4" 
                        />
                        <span className="text-sm font-semibold text-gray-700 bg-indigo-50 px-2 py-1 rounded">Mostrar Apenas Logins / Logouts</span>
                    </label>
                </div>
                <div className="overflow-x-auto print:overflow-visible print:h-auto print:block">
                    <table className="w-full text-left">
                        <thead className="bg-gray-100 border-b text-gray-600 text-sm print:bg-white">
                            <tr>
                                <th className="px-6 py-3 font-semibold">Funcionário</th>
                                <th className="px-6 py-3 font-semibold">Janela / Atividade</th>
                                <th className="px-6 py-3 font-semibold text-right">Data/Hora Real</th>
                                <th className="px-6 py-3 font-semibold text-center">Classificação</th>
                                <th className="px-6 py-3 font-semibold text-center print:hidden">Admin</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayedLogsFiltered.length === 0 && !loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                                        Nenhuma atividade registrada para os filtros selecionados.
                                    </td>
                                </tr>
                            ) : (
                                displayedLogsFiltered.map((log, i) => {
                                    const isMarcadoEntrada = marcacoes.some(m => String(m.sessao_id) === String(log.sessao_id) && m.tipo_marcacao === 'entrada');
                                    const isMarcadoRetorno = marcacoes.some(m => String(m.sessao_id) === String(log.sessao_id) && m.tipo_marcacao === 'retorno_almoco');
                                    const isMarcadoAlmoco = marcacoes.some(m => String(m.sessao_id) === String(log.sessao_id) && m.tipo_marcacao === 'almoco');
                                    const isMarcadoSaida = marcacoes.some(m => String(m.sessao_id) === String(log.sessao_id) && m.tipo_marcacao === 'saida');

                                    return (
                                        <tr key={i} className="border-b hover:bg-gray-50 transition-colors print:break-inside-avoid">
                                            <td className="px-6 py-4 font-bold text-gray-800">{log.nome_funcionario}</td>
                                            <td className="px-6 py-4 text-sm text-gray-700">{log.janela_ativa}</td>
                                            <td className="px-6 py-4 text-right font-mono text-sm text-gray-500">{log.hora_registro}</td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`px-2 py-1 text-xs font-semibold rounded-full print:border ${
                                                    log.status_atividade === 'Produtivo' ? 'bg-green-100 text-green-800 print:border-green-600' : 
                                                    log.status_atividade === 'Improdutivo' ? 'bg-red-100 text-red-800 print:border-red-600' : 
                                                    log.status_atividade === 'Ocioso' ? 'bg-orange-100 text-orange-800 print:border-orange-600' : 
                                                    log.status_atividade === 'Inativo' ? 'bg-gray-100 text-gray-800 print:border-gray-500' : 
                                                    log.status_atividade === 'Acesso / Login' ? 'bg-blue-100 text-blue-800 print:border-blue-600' : 
                                                    log.status_atividade === 'Saída / Logout' ? 'bg-gray-200 text-gray-800 print:border-gray-500' : 
                                                    log.status_atividade === 'Acesso Bloqueado' ? 'bg-red-600 text-white shadow-sm print:text-red-800 print:border-red-800' : 
                                                    log.status_atividade === 'Desconexão Forçada' ? 'bg-orange-100 text-orange-800 print:border-orange-600' : 
                                                    'bg-yellow-100 text-yellow-800 print:border-yellow-600'
                                                }`}>
                                                    {log.status_atividade}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-center print:hidden">
                                                {log.status_atividade === 'Acesso / Login' && (
                                                    <div className="flex gap-1 justify-center">
                                                        <button 
                                                            onClick={() => handleMarcacao(log.sessao_id, 'entrada', log.user_id)} 
                                                            className={`text-[9px] font-bold px-2 py-1 rounded transition-colors shadow-sm ${isMarcadoEntrada ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800 hover:bg-green-200'}`}
                                                        >
                                                            Entrada
                                                        </button>
                                                        <button 
                                                            onClick={() => handleMarcacao(log.sessao_id, 'retorno_almoco', log.user_id)} 
                                                            className={`text-[9px] font-bold px-2 py-1 rounded transition-colors shadow-sm ${isMarcadoRetorno ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800 hover:bg-blue-200'}`}
                                                        >
                                                            Retorno
                                                        </button>
                                                    </div>
                                                )}
                                                {log.status_atividade === 'Saída / Logout' && (
                                                    <div className="flex gap-1 justify-center">
                                                        <button 
                                                            onClick={() => handleMarcacao(log.sessao_id, 'almoco', log.user_id)} 
                                                            className={`text-[9px] font-bold px-2 py-1 rounded transition-colors shadow-sm ${isMarcadoAlmoco ? 'bg-red-600 text-white' : 'bg-red-100 text-red-800 hover:bg-red-200'}`}
                                                        >
                                                            Almoço
                                                        </button>
                                                        <button 
                                                            onClick={() => handleMarcacao(log.sessao_id, 'saida', log.user_id)} 
                                                            className={`text-[9px] font-bold px-2 py-1 rounded transition-colors shadow-sm ${isMarcadoSaida ? 'bg-gray-600 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                                                        >
                                                            Saída Final
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default MonitoringPage;